import type { FieldType } from '@kizen/api';
import {
  LLMPluginApp,
  LLMProviderModel,
} from 'components/Wizards/MetaFilters/LLMDecision/types';
import {
  CommonVariableDefinitionType,
  KizenNodeType,
  Step,
  StepDetailsType,
} from './automation';
import {
  AUTOMATION_VARIABLE_DATA_TYPES,
  CONTENT_TYPE,
  VARIABLE_CARDINALITIES,
} from '../constants';

export type AutomationVariableDataType = ValueOf<
  typeof AUTOMATION_VARIABLE_DATA_TYPES
>;

export type GenericOption<T = string> = {
  value: T;
  label: string;
};

export type Relation = {
  id: string;
  cardinality: string;
  relatedObject: string;
  relatedObjectName: string;
  relatedEntityName: string;
  name: string;
  description: string;
  entityName: string;
  objectName: string;
  objectClass: string;
  objectType: string;
  defaultOnActivities: boolean;
  deleted?: boolean;
  fieldId?: string;
  relationType:
    | 'one_to_one'
    | 'primary'
    | 'additional'
    | 'primary_for'
    | 'additional_for';
  fetchUrl: string;
};

export type FieldOption = {
  id: string;
  code: string;
  name: string;
  order: number;
  meta: Record<string, any>;
};

export type Rating = {
  id: string;
  maxLabel: string;
  maxValue: number;
  minLabel: string;
  minValue: number;
};

export type MoneyOption = {
  id: string;
  currency: string;
  symbol: string;
};

export type CustomObjectLike = {
  id: string;
  name: string;
  objectType: string;
  objectName: string;
  entityName: string;
  fetchUrl: string;
  associationSource: string;
  isRequired?: boolean;
};

export type Field = {
  id: string;
  displayName: string;
  moneyOptions: MoneyOption;
  name: string;
  customObjectField: null | {
    id: string;
    name: string;
    displayName: string;
    fieldType: KizenFieldType;
    isDefault: boolean;
    customObject: CustomObjectLike;
  };
  isDefault: boolean;
  isRequired: boolean;
  isReadOnly: boolean;
  isHidden: boolean;
  isDeletable: boolean;
  isHideable: boolean;
  order: number;
  meta: { cols?: number; isReadonly?: boolean };
  properties: {
    allowOptions: boolean;
    allowMultiple: boolean;
    allowRelations: boolean;
    allowAdd: boolean;
    type: string;
    targetColumn: string;
  };
  relation: Relation;
  rating: Rating;
  phonenumberOptions: any;
  decimalOptions: any;
  fieldType: KizenFieldType;

  options: FieldOption[];
  access: { edit: boolean; view: boolean };
  category: string;
  ratings?: Rating;
};

export type CategoryLike = {
  id: string;
  name: string;
  order: number;
};

export type RelatedField = Omit<Field, 'relation'> & {
  relation: Relation;
};

export type HistoryStatus =
  | 'completed'
  | 'debug_waiting'
  | 'failed'
  | 'paused_by_failure'
  | 'pending'
  | 'pending_throttled'
  | 'pending_retry'
  | 'paused_before_execution'
  | 'skipped_execution'
  | 'paused_after_execution'
  | 'cancelled'
  | 'waiting_for_branches'
  | 'blocked';

export type HistoryDetailedLog = Record<string, any>;

export type ExecutionHistory = {
  created: string;
  detailedLog: HistoryDetailedLog | null;
  executionTimeMs: any;
  id: string;
  index: number;
  numRunningAsyncJobs: number;
  parentStepId: string;
  parentHistoryId: string;
  status: HistoryStatus;
  stepId: string | null;
  stepOutput: null;
  triggerId: string | null;
  updated: string;
  step?: Step & { deleted?: boolean };
  trigger?: Step & { deleted?: boolean };
};

export type ErrorDetails = Pick<
  ExecutionHistory,
  'id' | 'stepId' | 'detailedLog'
>;

type Option = {
  label: string;
  value: string;
  code?: string;
  color?: string | undefined;
  id?: string;
};

export type ComparisonOption = {
  label: string;
  value: string;
  comparison: string;
  id?: string;
};

export type FieldValueMappingsValue = string | Option | ComparisonOption;

export type FieldValueMappings = {
  key: string;
  order: number;
  sourceValues: FieldValueMappingsValue[] | null;
  targetValue?: FieldValueMappingsValue;

  targetValues: FieldValueMappingsValue[] | null;
};

export type FieldValueMappingsValidation = {
  key: string;
  source: boolean;
  targetValues: boolean;
};

export type OptionWithFieldData = GenericOption & {
  fieldData: Field & { deleted: boolean };
};

export type RelatedFieldOptionData = GenericOption & {
  fieldData: Field & {
    deleted: boolean;
    type: KizenFieldType;
    emptyOnly?: boolean;
  };
  field?: Field;
  emptyOnly?: boolean;
};

export type KizenFieldType = FieldType;

export type DeletedOption = GenericOption & { deleted?: boolean };

export type KizenFieldData = Omit<Field, 'fieldType'> & {
  fieldType: KizenFieldType;
  relation: any;
};

type OptionWithDefault = GenericOption & {
  is_default: boolean;
};

type MetadataValue = {
  only_allow_multiple?: boolean;
  only_allow_single?: boolean;
};

export type VariableCardinality = ValueOf<typeof VARIABLE_CARDINALITIES>;

type MetadataVariableValue<T = MetadataValue> = Partial<
  Record<VariableCardinality, T>
>;

export type VariableCompatibility = Partial<
  Record<AutomationVariableDataType, MetadataVariableValue>
>;

export type AutomationMetaData = {
  variables?: {
    field_from_variable?: Record<KizenFieldType, VariableCompatibility>;
    variable_from_variable?: Record<
      AutomationVariableDataType,
      MetadataVariableValue<VariableCompatibility>
    >;
    variable_from_field?: Record<
      AutomationVariableDataType,
      MetadataVariableValue<Record<KizenFieldType, MetadataValue>>
    >;
    direct_field_to_variable?: Partial<
      Record<KizenFieldType, AutomationVariableDataType>
    >;
  };
  code_runners?: {
    runtime_choices: OptionWithDefault[];
  };
  llm?: {
    confidence_threshold_options: OptionWithDefault[];
    max_sequential_turns: { default: number; min: number; max: number | null };
    provider_model_details: {
      plugin_app: LLMPluginApp;
      models: LLMProviderModel[];
    }[];
  };
  detailed_log_templates: Omit<
    Partial<Record<KizenNodeType, string>>,
    'condition'
  > & {
    condition?: Record<StepDetailsType, string>;
  };
};

export type VariableOption = GenericOption & {
  meta: {
    name?: string;
    dataType: AutomationVariableDataType;
    dataSubtype?: any;
    sourceType?: CommonVariableDefinitionType;
    isArray: boolean;
    isArrayItemVariable: boolean;
    sourceArrayVariable: string | undefined;
    deleted?: boolean;
  };
  isVariable?: boolean;
  deleted?: boolean;
};

export type ContentType = (typeof CONTENT_TYPE)[keyof typeof CONTENT_TYPE];

export type ApiFieldToModifyOption = {
  deleted: boolean;
  displayName: string;
  fieldType: KizenFieldType;
  id: string;
  name: string;
  options?: Array<{
    id: string;
    code: string;
    name: string;
    order: number;
    meta: any;
  }>;
  relation: any;
};

export const StepMenuOptionsValues = {
  edit: 'edit',
  delete: 'delete',
  add_branch: 'add-branch',
  unskip: 'unskip',
  continue_with_branch: 'continue_with_branch',
  skip: 'skip',
  duplicate_step: 'duplicate-step',
  copy_step: 'copy-step',
  merge_branches: 'merge_branches',
  unmerge_branches: 'unmerge_branches',
  create_trigger: 'create_trigger',
  copy_step_id: 'copy-step-id',
} as const;

export type StepMenuOptions = ValueOf<typeof StepMenuOptionsValues>;
